import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bug,
  Sparkles,
  Receipt,
  ArrowLeft,
  ChevronRight,
  Loader2,
  FolderOpen,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSupportTickets } from "@/providers/SupportTicketsProvider";
import { useServiceOrders } from "@/providers/ServiceOrdersProvider";
import type { SupportTicketDto } from "@shared/types/SupportTicketDto";

type DialogStep = "choose" | "bug-form" | "feature-select";

interface CreateTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTicketCreated: (ticket: SupportTicketDto) => void;
}

const SUPPORT_OPTIONS = [
  {
    id: "bug",
    title: "Report a Bug",
    description: "Something isn't working as expected",
    icon: Bug,
    color: "text-red-400",
    bgColor: "bg-red-400/10",
    borderColor: "border-red-400/20",
  },
  {
    id: "feature",
    title: "Request New Feature or Change",
    description: "Suggest improvements or new functionality",
    icon: Sparkles,
    color: "text-blue-400",
    bgColor: "bg-blue-400/10",
    borderColor: "border-blue-400/20",
  },
  {
    id: "billing",
    title: "Billing / Contract Issue",
    description: "Questions about invoices or agreements",
    icon: Receipt,
    color: "text-amber-400",
    bgColor: "bg-amber-400/10",
    borderColor: "border-amber-400/20",
  },
];

export function CreateTicketDialog({
  open,
  onOpenChange,
  onTicketCreated,
}: CreateTicketDialogProps) {
  const navigate = useNavigate();
  const { createTicket } = useSupportTickets();
  const { serviceOrders, isLoading, fetchServiceOrders } = useServiceOrders();

  const [step, setStep] = useState<DialogStep>("choose");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [ticketName, setTicketName] = useState("");
  const [ticketDescription, setTicketDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchServiceOrders();
    }
  }, [open, serviceOrders, fetchServiceOrders]);

  function handleOptionSelect(optionId: string) {
    if (optionId === "bug") {
      setStep("bug-form");
    } else if (optionId === "feature") {
      setStep("feature-select");
    } else if (optionId === "billing") {
      onOpenChange(false);
      navigate("/disputes");
    }
  }

  function handleProjectSelectForFeature(
    projectId: string,
    projectName?: string,
  ) {
    setSelectedProjectId(projectId);
    const serviceOrder = serviceOrders.find(
      (so) => so.id === projectId || so.project.name === projectName,
    );
    if (serviceOrder) {
      onOpenChange(false);
      navigate(`/service-orders/${serviceOrder.id}/changeRequests`, {
        state: { openNewRequest: true },
      });
    }
  }

  async function handleSubmitBugReport() {
    if (!ticketName || !selectedProjectId) return;

    setIsSubmitting(true);
    try {
      const ticket = await createTicket({
        projectId: selectedProjectId,
        ticketName,
        ticketDescription,
      });
      resetForm();
      onTicketCreated(ticket);
    } catch (error) {
      console.error("Failed to create ticket:", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  function resetForm() {
    setStep("choose");
    setSelectedProjectId("");
    setTicketName("");
    setTicketDescription("");
  }

  function handleDialogClose(open: boolean) {
    if (!open) {
      resetForm();
    }
    onOpenChange(open);
  }

  function renderChooseStep() {
    return (
      <div className="space-y-3 py-4">
        <p className="text-sm text-muted-foreground mb-4">
          Select the type of support you need:
        </p>
        {SUPPORT_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              onClick={() => handleOptionSelect(option.id)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border ${option.borderColor} ${option.bgColor} hover:bg-white/5 transition-all duration-200 text-left group`}
            >
              <div
                className={`w-12 h-12 rounded-lg ${option.bgColor} flex items-center justify-center shrink-0`}
              >
                <Icon className={`w-6 h-6 ${option.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold">{option.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {option.description}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
            </button>
          );
        })}
      </div>
    );
  }

  function renderBugForm() {
    return (
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="project">Project</Label>
          <Select
            value={selectedProjectId}
            onValueChange={setSelectedProjectId}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={isLoading ? "Loading..." : "Select a project"}
              >
                {selectedProjectId && (
                  <span className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" />
                    {serviceOrders.find((p) => p.id === selectedProjectId)
                      ?.id ||
                      serviceOrders.find((so) => so.id === selectedProjectId)
                        ?.project.name}
                  </span>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {serviceOrders.length > 0
                ? serviceOrders.map((so) => (
                    <SelectItem key={so.id} value={so.id}>
                      {so.project.name}
                    </SelectItem>
                  ))
                : []}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ticketName">Issue Title</Label>
          <Input
            id="ticketName"
            placeholder="Brief description of the bug"
            value={ticketName}
            onChange={(e) => setTicketName(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ticketDescription">Details</Label>
          <Textarea
            id="ticketDescription"
            placeholder="Describe the bug in detail. Include steps to reproduce, expected behavior, and actual behavior..."
            rows={5}
            value={ticketDescription}
            onChange={(e) => setTicketDescription(e.target.value)}
          />
        </div>
      </div>
    );
  }

  function renderFeatureSelect() {
    const projectList =
      serviceOrders.length > 0
        ? serviceOrders.map((so) => ({ id: so.id, name: so.project.name }))
        : [];

    return (
      <div className="space-y-4 py-4">
        <p className="text-sm text-muted-foreground">
          Select the project you want to request changes for:
        </p>
        <div className="space-y-2">
          {projectList.map((project) => (
            <button
              key={project.id}
              onClick={() =>
                handleProjectSelectForFeature(project.id, project.name)
              }
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/5 transition-all duration-200 text-left group"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-400/10 flex items-center justify-center shrink-0">
                <FolderOpen className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium">{project.name}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
            </button>
          ))}
          {projectList.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No projects found
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step !== "choose" && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 mr-1"
                onClick={() => setStep("choose")}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {step === "choose" && "Create a Ticket"}
            {step === "bug-form" && "Report a Bug"}
            {step === "feature-select" && "Select Project"}
          </DialogTitle>
        </DialogHeader>

        {step === "choose" && renderChooseStep()}
        {step === "bug-form" && renderBugForm()}
        {step === "feature-select" && renderFeatureSelect()}

        {step === "bug-form" && (
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDialogClose(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitBugReport}
              disabled={!ticketName || !selectedProjectId || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Ticket"
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
